import {
  Tag, Building2, Calendar, CalendarDays, Package, ShoppingBag,
  ClipboardList, Kanban, Store, Archive, Clock, Users, Wand2,
  LayoutGrid, TrendingUp, Sliders, Truck, Settings, Ticket, Sparkles, Layers, Wallet,
  Contact, Shield, Radio, MapPinned, CreditCard, Smartphone, BadgePercent, LifeBuoy, Boxes,
  Gift, Award, Stamp, FileText, Megaphone, Globe,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Persona } from '../../lib/personas'
import { anyModuleEnabled, moduleEnabled } from '../../lib/modules'

export interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  /** Minimum permission required to show this nav item. */
  permission?: string
  /** Show when the user has any of these permissions. */
  anyPermission?: string[]
  /** Courier-surface permission (checked via hasNavPermission like others). */
  courierPermission?: string
  /** Hard-deny outside these personas (safety net on top of permissions). */
  personas?: Persona[]
  /**
   * Package module the item belongs to (`bookings`, `shifts`, …): hidden when the active
   * company's package doesn't have it. Permissions still apply on top. Platform items
   * never carry one.
   */
  module?: string
  /**
   * Shown when the package has at least one of these modules: an item that serves
   * several (Rewards: `promo_codes` or `loyalty`). Checked with {@link module} when
   * both are set.
   */
  anyModule?: string[]
  /** @deprecated use {@link permission} — maps to `company:write`. */
  ownerOnly?: boolean
  /** Live badge count key (resolved in Sidebar). */
  badgeKey?: 'pendingApplications' | 'orderActions' | 'supportNeedsReply' | 'supportUnread'
  end?: boolean
  subItems?: NavItem[]
}

export interface NavSection {
  label: string
  items: NavItem[]
  /** Visible only when active persona is Admin (and caller is platform admin). */
  adminOnly?: boolean
  /** Visible only when active persona is Courier. */
  courierOnly?: boolean
  /** Visible only when active persona is Partner. */
  partnerOnly?: boolean
}

export interface NavVisibilityContext {
  isPlatformAdmin?: boolean
  activePersona?: Persona | 'None'
  hasNavPermission: (code: string) => boolean
  /** /api/me `modules`: null or absent (an older core) means every module is on. */
  modules?: readonly string[] | null
}

/** Rewards is for whoever changes codes (catalog:write) or looks at them (orders:read). */
export const REWARDS_PERMISSIONS = ['catalog:write', 'orders:read']
/** Rewards shows when the package has promo codes or a loyalty card. */
export const REWARDS_MODULES = ['promo_codes', 'loyalty']

/** Compact merchant IA: short parents, nested leaves. Routes unchanged. */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: '',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: LayoutGrid, end: true, personas: ['Merchant', 'Manager', 'Worker'] },
      {
        label: 'Orders',
        to: '/orders/manage',
        icon: Kanban,
        permission: 'orders:read',
        personas: ['Merchant', 'Manager', 'Worker'],
        badgeKey: 'orderActions',
        subItems: [
          {
            label: 'Board',
            to: '/orders/manage',
            icon: Kanban,
            permission: 'orders:read',
            personas: ['Merchant', 'Manager', 'Worker'],
          },
          {
            label: 'List',
            to: '/orders',
            icon: ClipboardList,
            end: true,
            permission: 'orders:read',
            personas: ['Merchant', 'Manager', 'Worker'],
          },
        ],
      },
      {
        label: 'Clients',
        to: '/clients',
        icon: Contact,
        permission: 'orders:read',
        personas: ['Merchant', 'Manager'],
        subItems: [
          {
            label: 'Tags',
            to: '/clients/tags',
            icon: Tag,
            permission: 'orders:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Invite links',
            to: '/clients/invites',
            icon: Ticket,
            permission: 'orders:read',
            personas: ['Merchant', 'Manager'],
          },
        ],
      },
      {
        // Every bonus a store gives a client (docs/rewards.md). Readers look
        // (orders:read), catalog:write changes. Each phase adds one entry.
        label: 'Rewards',
        to: '/rewards',
        icon: Gift,
        anyPermission: REWARDS_PERMISSIONS,
        anyModule: REWARDS_MODULES,
        personas: ['Merchant', 'Manager'],
        subItems: [
          {
            label: 'Overview',
            to: '/rewards',
            icon: LayoutGrid,
            end: true,
            anyPermission: REWARDS_PERMISSIONS,
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Promo codes',
            to: '/rewards/promo-codes',
            icon: BadgePercent,
            anyPermission: REWARDS_PERMISSIONS,
            module: 'promo_codes',
            personas: ['Merchant', 'Manager'],
          },
          {
            // Rewards phase 2: the store's stamp or spend card.
            label: 'Loyalty card',
            to: '/rewards/loyalty',
            icon: Stamp,
            anyPermission: REWARDS_PERMISSIONS,
            module: 'loyalty',
            personas: ['Merchant', 'Manager'],
          },
          {
            // Rewards phase 3: campaigns TOU runs; the store joins with its offerings.
            label: 'TOU campaigns',
            to: '/rewards/campaigns',
            icon: Megaphone,
            anyPermission: REWARDS_PERMISSIONS,
            module: 'promo_codes',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Awards',
            to: '/rewards/awards',
            icon: Award,
            anyPermission: REWARDS_PERMISSIONS,
            personas: ['Merchant', 'Manager'],
          },
          {
            // The same page as Clients › Invite links.
            label: 'Invite links',
            to: '/rewards/invites',
            icon: Ticket,
            permission: 'orders:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Report',
            to: '/rewards/report',
            icon: TrendingUp,
            anyPermission: REWARDS_PERMISSIONS,
            personas: ['Merchant', 'Manager'],
          },
        ],
      },
      {
        label: 'Schedule',
        to: '/calendar',
        icon: Calendar,
        anyPermission: ['bookings:read', 'shifts:read'],
        personas: ['Merchant', 'Manager', 'Worker'],
        subItems: [
          {
            label: 'Calendar',
            to: '/calendar',
            icon: Calendar,
            permission: 'bookings:read',
            module: 'bookings',
            personas: ['Merchant', 'Manager', 'Worker'],
          },
          {
            label: 'Events',
            to: '/events',
            icon: Ticket,
            end: true,
            permission: 'bookings:read',
            module: 'events',
            personas: ['Merchant', 'Manager', 'Worker'],
          },
          {
            label: 'Shifts',
            to: '/shifts',
            icon: Clock,
            end: true,
            permission: 'shifts:read',
            module: 'shifts',
            personas: ['Merchant', 'Manager', 'Worker'],
          },
          {
            label: 'Generate Shifts',
            to: '/shifts/generate',
            icon: Wand2,
            permission: 'schedule:generate',
            module: 'shifts',
            personas: ['Merchant', 'Manager'],
          },
        ],
      },
      {
        label: 'Catalog',
        to: '/categories',
        icon: Package,
        anyPermission: ['catalog:read', 'stock:read', 'plans:read'],
        personas: ['Merchant', 'Manager'],
        subItems: [
          {
            label: 'Categories',
            to: '/categories',
            icon: Tag,
            permission: 'catalog:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Inventory',
            to: '/inventory',
            icon: Package,
            permission: 'catalog:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Options & add-ons',
            to: '/addons',
            icon: Layers,
            permission: 'catalog:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Offerings',
            to: '/offerings',
            icon: ShoppingBag,
            permission: 'catalog:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Stock',
            to: '/stock',
            icon: Archive,
            permission: 'stock:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Subscriptions',
            to: '/plans',
            icon: ClipboardList,
            end: true,
            permission: 'plans:read',
            module: 'client_plans',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Plan Configurations',
            to: '/plans/configs',
            icon: Sliders,
            permission: 'plans:read',
            module: 'client_plans',
            personas: ['Merchant', 'Manager'],
          },
        ],
      },
      {
        label: 'Business',
        to: '/company',
        icon: Building2,
        anyPermission: ['company:read', 'stores:read', 'workers:write', 'managers:write', 'wallet:read'],
        personas: ['Merchant', 'Manager'],
        subItems: [
          {
            label: 'Company',
            to: '/company',
            icon: Building2,
            end: true,
            permission: 'company:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            // The company's shop in TOU, with its own link (ADR-W19). Editing needs company:write.
            label: 'Shop page',
            to: '/company/shop',
            icon: Globe,
            permission: 'company:read',
            module: 'shop_page',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Package',
            to: '/company/package',
            icon: Boxes,
            permission: 'company:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Invoices',
            to: '/company/invoices',
            icon: FileText,
            permission: 'company:read',
            module: 'invoices',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Public holidays',
            to: '/company/holidays',
            icon: CalendarDays,
            permission: 'company:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Stores',
            to: '/stores',
            icon: Store,
            permission: 'stores:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            // service-core keeps devices to the owner and managers by company role,
            // not a permission code, so only the persona gates it (never a Worker).
            label: 'Devices',
            to: '/devices',
            icon: Smartphone,
            personas: ['Merchant', 'Manager'],
            module: 'devices',
          },
          {
            label: 'Wallet',
            to: '/wallet',
            icon: Wallet,
            permission: 'wallet:read',
            personas: ['Merchant', 'Manager'],
          },
          {
            label: 'Team',
            to: '/workers',
            icon: Users,
            anyPermission: ['workers:write', 'managers:write'],
            personas: ['Merchant', 'Manager'],
          },
        ],
      },
      {
        label: 'Reporting',
        to: '/reporting',
        icon: TrendingUp,
        permission: 'reports:read',
        personas: ['Merchant', 'Manager'],
        module: 'reports',
      },
    ],
  },
  {
    label: 'Partner',
    partnerOnly: true,
    items: [
      {
        label: 'Orders',
        to: '/partner/orders',
        icon: Truck,
        permission: 'partner:orders:read',
        personas: ['Partner'],
      },
      {
        label: 'Zones & rates',
        to: '/partner/coverage',
        icon: MapPinned,
        anyPermission: ['partner:zones:read', 'partner:rates:read', 'partner:hours:read'],
        personas: ['Partner'],
      },
    ],
  },
  {
    label: 'Courier',
    courierOnly: true,
    items: [
      {
        label: 'Deliveries',
        to: '/courier/deliveries',
        icon: Truck,
        courierPermission: 'courier:orders:read',
        personas: ['Courier'],
      },
      {
        label: 'Earnings',
        to: '/courier/earnings',
        icon: Wallet,
        courierPermission: 'courier:orders:read',
        personas: ['Courier'],
      },
    ],
  },
  {
    label: 'Platform',
    adminOnly: true,
    items: [
      { label: 'Overview', to: '/platform', icon: LayoutGrid, end: true, personas: ['Admin'] },
      {
        label: 'Companies',
        to: '/platform/companies',
        icon: Building2,
        permission: 'admin:companies:read',
        badgeKey: 'pendingApplications',
        personas: ['Admin'],
      },
      {
        label: 'Packages',
        to: '/platform/packages',
        icon: Boxes,
        permission: 'admin:companies:read',
        personas: ['Admin'],
      },
      {
        label: 'Catalog',
        to: '/platform/catalog',
        icon: Tag,
        permission: 'admin:companies:read',
        personas: ['Admin'],
      },
      {
        label: 'Users',
        to: '/platform/users',
        icon: Users,
        permission: 'admin:users:read',
        personas: ['Admin'],
      },
      {
        label: 'Couriers',
        to: '/platform/couriers',
        icon: Truck,
        permission: 'admin:users:read',
        personas: ['Admin'],
      },
      {
        label: 'Dispatch',
        to: '/platform/dispatch',
        icon: Radio,
        permission: 'admin:users:read',
        personas: ['Admin'],
      },
      {
        // The inbox for every requester's Help & support; the badge is GET /api/admin/support/summary's needsReply.
        label: 'Support',
        to: '/platform/support',
        icon: LifeBuoy,
        permission: 'admin:support:read',
        personas: ['Admin'],
        badgeKey: 'supportNeedsReply',
      },
      {
        label: 'Devices',
        to: '/platform/devices',
        icon: Smartphone,
        permission: 'admin:users:read',
        personas: ['Admin'],
      },
      {
        label: 'Payments',
        to: '/platform/payments',
        icon: CreditCard,
        permission: 'admin:finance:read',
        personas: ['Admin'],
      },
      {
        label: 'Wallet',
        to: '/platform/wallet',
        icon: Wallet,
        permission: 'admin:finance:read',
        personas: ['Admin'],
      },
      {
        // A group: it shows while one of its entries does. Automatic offers
        // and Gift cards join it as one entry each.
        label: 'Rewards',
        to: '/platform/rewards/promo-codes',
        icon: Gift,
        personas: ['Admin'],
        subItems: [
          {
            label: 'Promo codes',
            to: '/platform/rewards/promo-codes',
            icon: BadgePercent,
            permission: 'admin:finance:read',
            personas: ['Admin'],
          },
          {
            label: 'Loyalty',
            to: '/platform/rewards/loyalty',
            icon: Stamp,
            permission: 'admin:finance:read',
            personas: ['Admin'],
          },
          {
            label: 'TOU campaigns',
            to: '/platform/rewards/campaigns',
            icon: Megaphone,
            permission: 'admin:finance:read',
            personas: ['Admin'],
          },
        ],
      },
      {
        label: 'Invoices',
        to: '/platform/invoices',
        icon: FileText,
        permission: 'admin:finance:read',
        personas: ['Admin'],
      },
      {
        label: 'Rights',
        to: '/platform/rights',
        icon: Shield,
        permission: 'admin:system:read',
        personas: ['Admin'],
      },
      {
        label: 'System',
        to: '/platform/system',
        icon: Settings,
        permission: 'admin:system:read',
        personas: ['Admin'],
      },
      {
        label: 'AI',
        to: '/platform/ai',
        icon: Sparkles,
        permission: 'admin:system:read',
        personas: ['Admin'],
      },
    ],
  },
]

/**
 * Help & support, at the bottom of the sidebar for everyone who asks TOU
 * support: shops (Merchant, Manager, Worker), couriers and partner desks — not
 * the platform, which answers in Platform › Support. Not in NAV_SECTIONS: the
 * Sidebar shows it only once GET /api/support/options answers, so it stays
 * hidden on a server without support. The badge is GET /api/support/unread-count.
 */
export const HELP_SUPPORT_ITEM: NavItem = {
  label: 'Help & support',
  to: '/support',
  icon: LifeBuoy,
  personas: ['Merchant', 'Manager', 'Worker', 'Courier', 'Partner'],
  badgeKey: 'supportUnread',
}

/**
 * The same foot link once the server has the help centre: it opens the help
 * landing, which holds New request and Your requests. Without help (a 404 from
 * /api/help/articles) the side menu falls back to HELP_SUPPORT_ITEM.
 */
export const HELP_CENTRE_ITEM: NavItem = {
  ...HELP_SUPPORT_ITEM,
  to: '/help',
}

export function pathMatchesItem(pathname: string, item: NavItem): boolean {
  const normalized = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  if (item.end) {
    return normalized === item.to
  }
  return normalized === item.to || normalized.startsWith(`${item.to}/`)
}

/** True if pathname is this item or any nested descendant. */
export function pathInSubtree(pathname: string, item: NavItem): boolean {
  if (pathMatchesItem(pathname, item)) return true
  return item.subItems?.some((sub) => pathInSubtree(pathname, sub)) ?? false
}

function selfPermissionVisible(
  item: NavItem,
  check: (code: string) => boolean,
  isPlatformAdmin: boolean,
): boolean {
  if (item.courierPermission) return check(item.courierPermission)
  if (item.anyPermission?.length) return item.anyPermission.some(check)
  if (item.permission?.startsWith('admin:')) {
    return Boolean(isPlatformAdmin) && check(item.permission)
  }
  if (item.permission) return check(item.permission)
  if (item.ownerOnly) return check('company:write')
  return true
}

export function navItemVisible(
  item: NavItem,
  hasPermission: (code: string) => boolean,
  ctx: NavVisibilityContext | boolean = false,
): boolean {
  const context: NavVisibilityContext =
    typeof ctx === 'boolean'
      ? { isPlatformAdmin: ctx, hasNavPermission: hasPermission, activePersona: 'None' }
      : ctx

  const { isPlatformAdmin = false, activePersona = 'None', hasNavPermission, modules } = context
  const check = hasNavPermission ?? hasPermission

  if (item.personas?.length && activePersona !== 'None' && !item.personas.includes(activePersona as Persona)) {
    return false
  }

  // Not in the company's package: hidden whatever the permissions say.
  if (item.module && !moduleEnabled(modules, item.module)) return false
  if (item.anyModule?.length && !anyModuleEnabled(modules, item.anyModule)) return false

  const selfOk = selfPermissionVisible(item, check, isPlatformAdmin)

  if (item.subItems?.length) {
    const anyChild = item.subItems.some((sub) => navItemVisible(sub, hasPermission, context))
    // Parent group shows when self gates pass and at least one child is visible.
    return selfOk && anyChild
  }

  return selfOk
}

export function navSectionVisible(
  section: NavSection,
  hasPermission: (code: string) => boolean,
  ctx: NavVisibilityContext | boolean = false,
): boolean {
  const context: NavVisibilityContext =
    typeof ctx === 'boolean'
      ? { isPlatformAdmin: ctx, hasNavPermission: hasPermission, activePersona: 'None' }
      : ctx

  if (section.adminOnly) {
    if (!context.isPlatformAdmin || context.activePersona !== 'Admin') return false
  }
  if (section.courierOnly && context.activePersona !== 'Courier') return false
  if (section.partnerOnly && context.activePersona !== 'Partner') return false

  return section.items.some((item) => navItemVisible(item, hasPermission, context))
}

/** Collect all top-level items that contain the current path (for auto-expand). */
export function findExpandedParentKeys(
  sections: NavSection[],
  pathname: string,
  hasPermission: (code: string) => boolean,
  ctx: NavVisibilityContext,
): string[] {
  const keys: string[] = []
  for (const section of sections) {
    if (!navSectionVisible(section, hasPermission, ctx)) continue
    for (const item of section.items) {
      if (!navItemVisible(item, hasPermission, ctx)) continue
      if (item.subItems?.length && pathInSubtree(pathname, item)) {
        keys.push(item.to)
      }
    }
  }
  return keys
}

export interface FlatNavCommand {
  label: string
  to: string
  /** Parent label for nested items (e.g. Orders → Board). */
  group?: string
  icon: LucideIcon
}

/** Flatten persona-visible leaf routes for the command palette. */
export function flattenVisibleNavItems(
  sections: NavSection[],
  hasPermission: (code: string) => boolean,
  ctx: NavVisibilityContext,
): FlatNavCommand[] {
  const out: FlatNavCommand[] = []

  const visit = (item: NavItem, group?: string) => {
    if (!navItemVisible(item, hasPermission, ctx)) return
    const visibleChildren =
      item.subItems?.filter((sub) => navItemVisible(sub, hasPermission, ctx)) ?? []
    if (visibleChildren.length > 0) {
      for (const sub of visibleChildren) {
        visit(sub, item.label)
      }
      return
    }
    out.push({ label: item.label, to: item.to, group, icon: item.icon })
  }

  for (const section of sections) {
    if (!navSectionVisible(section, hasPermission, ctx)) continue
    for (const item of section.items) {
      visit(item, section.label || undefined)
    }
  }
  return out
}
